import { getSecureUrl } from './url';

const DIVERSITY_MODE = 'diversity';
const types = {
  stylesheet: {
    name: 'stylesheet',
    tagName: 'link',
    typeVal: 'stylesheet',
    srcAttr: 'href',
    typeAttr: 'rel',
  },
  script: {
    name: 'script',
    tagName: 'script',
    typeVal: 'text/javascript',
    srcAttr: 'src',
    innerCodeAttr: 'innerText',
    typeAttr: 'type',
  },
  image: {
    name: 'image',
    tagName: 'img',
    srcAttr: 'src',
  },
};

const promises = {};
const fetched = {};
const loadedAssets = new Map();

function AssetLoader(assets, opts = {}) {
  this.assets = assets;
  this.opts = opts;

  const findGlobalSrc = (variable, next = window) => {
    variable = Array.isArray(variable) ? variable : variable.split('.');
    const key = variable.shift();

    if (!(key && next[key])) {
      return;
    }

    if (typeof next[key] !== 'string') {
      return findGlobalSrc(variable, next[key]);
    }
    return next[key];
  };

  const resolveAsset = (parts, { resolve, reject }, finish) => {
    const asset = parts.shift();
    if (!asset && finish) {
      return finish();
    }

    Promise.all(getAssets(asset))
      .then(() => {
        if (this.opts.mode !== DIVERSITY_MODE) {
          resolveAsset(parts, { resolve, reject }, resolve);
        } else {
          resolve();
        }
      })
      .catch(() => {
        if (this.opts.mode === DIVERSITY_MODE) {
          resolveAsset(parts, { resolve, reject }, reject);
        } else {
          reject();
        }
      });
  };

  const getAssets = (parts) => {
    if (!Array.isArray(parts)) {
      parts = [parts];
    }

    return parts.map(({ src, id, prepend, globalSrc, type = types.script.name }) => {
      const assetType = types[type];

      if (globalSrc && !src) {
        src = findGlobalSrc(globalSrc);
      }

      if (!(src && id && types[type])) {
        return Promise.reject();
      }

      if (fetched[id]) {
        return Promise.resolve();
      }

      promises[id] = promises[id] || {};

      if (promises[id].pending) {
        return promises[id].value;
      }

      promises[id].value = new Promise((resolve, reject) => {
        promises[id].pending = true;
        const asset = document.createElement(assetType.tagName);
        if (type === types.script.name) {
          if (this.opts.async) {
            asset.setAttribute('async', 'async');
          }

          if (this.opts.defer) {
            asset.setAttribute('defer', 'defer');
          }

          if (prepend) {
            const codeToPrepend = document.createElement(assetType.tagName);
            codeToPrepend[assetType.typeAttr] = assetType.typeVal;
            codeToPrepend[assetType.innerCodeAttr] = prepend;
            document.head.appendChild(codeToPrepend);
          }
        }

        asset[assetType.srcAttr] = getSecureUrl(src);
        asset[assetType.typeAttr] = assetType.typeVal;

        asset.onload = () => {
          resolve();
          promises[id].pending = false;
          fetched[id] = true;
        };

        asset.onerror = () => {
          promises[id].pending = false;
          reject();
        };

        if (type !== types.image.name) {
          document.head.appendChild(asset);
          loadedAssets.set(id, asset);
        }
      });

      return promises[id].value;
    });
  };

  this.load = (parts = this.assets, opts = this.opts || {}) => {
    this.opts = opts;

    if (!Array.isArray(parts)) {
      parts = [parts];
    }

    return new Promise((resolve, reject) => {
      resolveAsset([...parts], { resolve, reject });
    });
  };

  this.unload = (parts = []) => {
    if (!Array.isArray(parts)) {
      parts = [parts];
    }

    parts.forEach(({ id }) => {
      const script = loadedAssets.get(id);

      promises[id] = null;
      fetched[id] = false;
      script?.parentNode.removeChild(script);
      loadedAssets.delete(id);
    });
  };
}

export default AssetLoader;
